import { useState, useCallback } from 'react';
import { 
  analyzeContentCompliance, 
  generateCompliantContent, 
  explainRegulation, 
  analyzeSimilarContent 
} from '../services/complianceAnalysisService';

/**
 * Custom hook for compliance analysis functionality
 * Manages state and provides methods for compliance operations
 */
export const useComplianceAnalysis = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [complianceHistory, setComplianceHistory] = useState([]);
  const [error, setError] = useState(null);

  /**
   * Analyzes content for compliance violations
   */
  const analyzeContent = useCallback(async (content, platform = 'social_media', contentType = 'marketing') => {
    if (!content?.trim()) {
      setError('Content is required for analysis');
      return null;
    }

    setIsAnalyzing(true);
    setError(null);
    
    try {
      const result = await analyzeContentCompliance(content, platform, contentType);
      
      setAnalysis(result);
      
      // Add to history
      const historyEntry = {
        id: `analysis-${Date.now()}`,
        content,
        platform,
        contentType,
        result,
        timestamp: new Date()?.toISOString()
      };
      
      setComplianceHistory(prev => [historyEntry, ...prev?.slice(0, 9)]); // Keep last 10
      
      return result;
    } catch (err) {
      setError(err?.message);
      console.error('Compliance analysis error:', err);
      return null;
    } finally {
      setIsAnalyzing(false);
    }
  }, []);

  /**
   * Generates compliant content suggestions
   */
  const generateSuggestions = useCallback(async (originalContent, violations, platform) => {
    if (!originalContent?.trim() || !violations?.length) {
      setError('Original content and violations are required');
      return null;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      const suggestions = await generateCompliantContent(originalContent, violations, platform);
      return suggestions;
    } catch (err) {
      setError(err?.message);
      console.error('Content generation error:', err);
      return null;
    } finally {
      setIsAnalyzing(false);
    }
  }, []);

  /**
   * Explains a specific regulation
   */
  const explainRule = useCallback(async (regulation, context = '') => {
    if (!regulation?.trim()) {
      setError('Regulation is required');
      return null;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      const explanation = await explainRegulation(regulation, context);
      return explanation;
    } catch (err) {
      setError(err?.message);
      console.error('Regulation explanation error:', err);
      return null;
    } finally {
      setIsAnalyzing(false);
    }
  }, []);

  /**
   * Analyzes similar content for patterns
   */
  const analyzeSimilar = useCallback(async (currentContent, historicalContent = []) => {
    if (!currentContent?.trim()) {
      setError('Current content is required');
      return null;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      const analysis = await analyzeSimilarContent(currentContent, historicalContent);
      return analysis;
    } catch (err) {
      setError(err?.message);
      console.error('Similar content analysis error:', err);
      return null;
    } finally {
      setIsAnalyzing(false);
    }
  }, []);

  /**
   * Clears current analysis and error state
   */
  const clearAnalysis = useCallback(() => {
    setAnalysis(null);
    setError(null);
  }, []);

  /**
   * Clears error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Gets analysis history
   */
  const getHistory = useCallback(() => {
    return complianceHistory;
  }, [complianceHistory]);

  return {
    // State
    isAnalyzing,
    analysis,
    error,
    complianceHistory,
    
    // Methods
    analyzeContent,
    generateSuggestions,
    explainRule,
    analyzeSimilar,
    clearAnalysis,
    clearError,
    getHistory
  };
};

export default useComplianceAnalysis;